import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface HeatmapCell {
  label: string;
  value: number;
  sublabel?: string;
}

interface HeatmapGridProps {
  title: string;
  data: HeatmapCell[];
  columns?: number;
  colorScale?: [string, string];
}

const HeatmapGrid: React.FC<HeatmapGridProps> = ({
  title,
  data,
  columns = 4,
  colorScale = ['#EFF6FF', '#1E40AF'],
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));

  const getColor = (value: number) => {
    const normalized = (value - minValue) / (maxValue - minValue || 1);
    
    // Interpolate between two colors
    const r1 = parseInt(colorScale[0].slice(1, 3), 16);
    const g1 = parseInt(colorScale[0].slice(3, 5), 16);
    const b1 = parseInt(colorScale[0].slice(5, 7), 16);
    
    const r2 = parseInt(colorScale[1].slice(1, 3), 16);
    const g2 = parseInt(colorScale[1].slice(3, 5), 16);
    const b2 = parseInt(colorScale[1].slice(5, 7), 16);
    
    const r = Math.round(r1 + (r2 - r1) * normalized);
    const g = Math.round(g1 + (g2 - g1) * normalized);
    const b = Math.round(b1 + (b2 - b1) * normalized);
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <motion.div
      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-lg font-bold text-gray-900 mb-6">{title}</h3>

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {data.map((cell, index) => {
          const bgColor = getColor(cell.value);
          const isLight = cell.value < (maxValue + minValue) / 2;

          return (
            <motion.div
              key={index}
              className="relative aspect-square rounded-xl overflow-hidden cursor-pointer"
              style={{ backgroundColor: bgColor }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05, type: 'spring', stiffness: 200 }}
              whileHover={{ scale: 1.05, zIndex: 10 }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
            >
              {/* Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
                <motion.div
                  className={`text-xs font-medium mb-1 text-center ${
                    isLight ? 'text-gray-700' : 'text-white'
                  }`}
                  animate={{ scale: hoveredIndex === index ? 1.1 : 1 }}
                >
                  {cell.label}
                </motion.div>
                <motion.div
                  className={`text-2xl font-bold ${
                    isLight ? 'text-gray-900' : 'text-white'
                  }`}
                  animate={{ scale: hoveredIndex === index ? 1.2 : 1 }}
                >
                  {cell.value}
                </motion.div>
                {cell.sublabel && (
                  <motion.div
                    className={`text-xs mt-1 ${
                      isLight ? 'text-gray-600' : 'text-white/80'
                    }`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: hoveredIndex === index ? 1 : 0.7 }}
                  >
                    {cell.sublabel}
                  </motion.div>
                )}
              </div>

              {/* Hover Overlay */}
              <motion.div
                className="absolute inset-0 bg-white"
                initial={{ opacity: 0 }}
                animate={{ opacity: hoveredIndex === index ? 0.1 : 0 }}
                transition={{ duration: 0.2 }}
              />

              {/* Pulse Effect */}
              {hoveredIndex === index && (
                <motion.div
                  className="absolute inset-0 border-2 border-white rounded-xl"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: [0, 1, 0], scale: [0.8, 1.1, 1.2] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center justify-between">
        <span className="text-xs text-gray-500">Low</span>
        <div className="flex-1 mx-4 h-3 rounded-full" style={{
          background: `linear-gradient(to right, ${colorScale[0]}, ${colorScale[1]})`
        }} />
        <span className="text-xs text-gray-500">High</span>
      </div>
    </motion.div>
  );
};

export default HeatmapGrid;

